import { css } from '@emotion/css';
import { TFunction } from 'i18next';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { RootState } from '../../features';
import { actions, UserErrorType } from '../../features/user';
import { sendEvent } from '../../lib/google-analytics';
import { UserList } from '../PlayerSelect/UserList';
import { ConfirmButton } from '../shared/buttons/ConfirmButton';
import { TitleHeader } from '../shared/TitleHeader';

const playerSwitchStyles = css`
  display: flex;
  flex-direction: column;
  background: #f5f1e3;
  font-size: 16px;
  color: #4d3906;
  width: 100%;
  height: 100%;

  .body {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 480px;
    overflow: hidden;
    margin: 0 auto;
  }
  .desc {
    font-size: 14px;
    margin: 24px 32px 12px;
  }
  .notice {
    font-size: 12px;
    margin: 16px 0;
    text-align: center;
    color: #938666;
  }
  .user-list {
    flex: 1 1 auto;
    overflow-y: scroll;
  }
  .button {
    flex: 0 0 auto;
    width: 100%;
    max-width: 480px;
    margin: 0 auto;
    padding: 12px 16px;
    padding: 12px 16px calc(env(safe-area-inset-bottom) + 20px);
  }
  .button.loading {
    pointer-events: none;
    opacity: 0.5;
  }
  .error {
    font-size: 12px;
    text-align: center;
    color: #e60012;
    padding: 1em 2em;
  }
`;

export const PlayerSwitch: React.FC = () => {
  const { t, i18n } = useTranslation(['settings', 'translation']);
  const { users, selectedUserId, selectStatus, isLoading, errorType } = useSelector(
    (state: RootState) => state.user
  );
  const dispatch = useDispatch();
  const [checkedUserId, setCheckedUserId] = useState<string | undefined>(selectedUserId);
  const history = useHistory();
  const error = getText(t, errorType);

  const handleUserClick = useCallback(
    (userId: string) => () => {
      setCheckedUserId(userId);
    },
    [setCheckedUserId]
  );

  useEffect(() => {
    dispatch(actions.updateUserList());
  }, [dispatch]);

  useEffect(() => {
    if (selectStatus === 'succeeded') {
      // Workaround: ホーム画面に戻り，historyスタックの最初の状態になりたい。
      history.go(-2);
    }
  }, [selectStatus, history]);

  const handleConfirmClick = () => {
    if (checkedUserId && checkedUserId !== selectedUserId) {
      const { language } = i18n;
      dispatch(actions.reselectUser({ selectedUserId: checkedUserId, language }));
      sendEvent('settings_complete_select_user', 'click');
    }
  };

  const isSelecting = selectStatus === 'loading';

  return (
    <main className={playerSwitchStyles}>
      <TitleHeader
        title={t('reselect_user')}
        color="#dbcca7"
        backLabel={t('aria_back_to_settings')}
      />
      <div className="body">
        <p className="desc">{t('reselect_user_00')}</p>
        <section className="user-list">
          <UserList
            {...{
              users,
              disabled: isLoading || isSelecting,
              checkedUserId,
              selectedUserId,
              handleUserClick
            }}
          />
        </section>
      </div>
      {errorType && <p className="error">{error}</p>}
      <div className={`button ${isSelecting ? 'loading' : ''}`}>
        <ConfirmButton
          onClick={handleConfirmClick}
          loading={isSelecting || isLoading}
          disabled={selectedUserId === checkedUserId}
        >
          {t('translation:dialog_button_ok')}
        </ConfirmButton>
        <p className="notice">{t('reselect_user_01')}</p>
      </div>
    </main>
  );
};

// helper function
function getText(t: TFunction, errorType?: UserErrorType) {
  switch (errorType) {
    case 'player-profile-not-found':
      return t('translation:error_player_profile_not_found');
    case 'player-not-in-island':
      return t('translation:error_player_not_in_island');
    case 'unstable-network':
      return t('translation:error_unstable_network');
    case 'unknown':
    default:
      return t('translation:error_unknown');
  }
}
